import React, { useState, useEffect } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { Container, Row, Col, Card, Table, Button, Form, Badge } from 'react-bootstrap';
import { useAuth } from '../hooks/useAuth';
import { formatDateLong } from '../utils/dateFormatter';

/**
 * AssetReport displays a Double Declining Balance depreciation schedule
 * for all fleet vehicles that have a purchase cost on record.
 * Accessible to admin and auditor roles.
 */
export function AssetReport() {
  const { apiCall } = useOutletContext();
  const { user } = useAuth();

  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedPeriod, setSelectedPeriod] = useState('annual'); // 'annual' | 'Q1'–'Q4'
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [stores, setStores] = useState([]);

  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load store list for the filter dropdown (admin/auditor only)
  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'auditor') {
      loadStores();
    }
  }, [user]);

  useEffect(() => {
    loadReport();
  }, [selectedYear, selectedPeriod, selectedStoreId]);

  const loadStores = async () => {
    try {
      const token = localStorage.getItem('tegrat_auth_token');
      const response = await fetch(`${API_BASE_URL}/stores`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setStores(data.stores || data.data || []);
      }
    } catch (err) {
      console.error('Error loading stores:', err);
    }
  };

  const loadReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('tegrat_auth_token');

      let url = `${API_BASE_URL}/analytics/asset-report?year=${selectedYear}`;
      if (selectedPeriod !== 'annual') {
        url += `&quarter=${selectedPeriod.replace('Q', '')}`;
      }
      if (selectedStoreId) {
        url += `&store_id=${selectedStoreId}`;
      }

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to load asset report');
      }

      const data = await response.json();
      setReportData(data);
    } catch (err) {
      console.error('Error loading asset report:', err);
      setError('Failed to load asset report data');
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = () => {
    if (!reportData?.data?.length) return;

    const headers = [
      'Vehicle ID', 'VIN', 'Make', 'Model', 'Year', 'Store', 'Status',
      'Acquisition Date', 'Purchase Cost', 'Years in Service',
      'Opening Book Value', 'Period Depreciation', 'Accumulated Depreciation',
      'Closing Book Value', 'Fully Depreciated',
    ];

    const rows = reportData.data.map(v => [
      v.vehicle_id, v.vin, v.make, v.model, v.year, v.store, v.status,
      v.acquisition_date, v.purchase_cost, v.years_in_service,
      v.opening_book_value, v.period_depreciation, v.accumulated_depreciation,
      v.closing_book_value, v.fully_depreciated ? 'Yes' : 'No',
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = `asset-report-${reportData.period}.csv`;
    a.click();
    URL.revokeObjectURL(blobUrl);
  };

  // Year options: 2025 up through next year
  const yearOptions = [];
  for (let y = 2025; y <= now.getFullYear() + 1; y++) {
    yearOptions.push(y);
  }

  const fmt = (n) =>
    `$${parseFloat(n || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const statusVariant = (status) => {
    if (status === 'rented') return 'primary';
    if (status === 'maintenance') return 'warning';
    if (status === 'rto') return 'info';
    return 'success';
  };

  const summary = reportData?.summary;

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Row className="mb-4 align-items-center">
        <Col>
          <h2>Asset Depreciation Report</h2>
          <small className="text-muted">
            Double Declining Balance &mdash; 5-year useful life, 40% annual rate
          </small>
        </Col>
        <Col className="text-end">
          <Button
            variant="outline-secondary"
            onClick={exportCsv}
            disabled={!reportData?.data?.length}
          >
            Export CSV
          </Button>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="mb-4">
        <Card.Body>
          <Row className="align-items-end g-3">
            <Col md={2}>
              <Form.Label>Year</Form.Label>
              <Form.Select
                value={selectedYear}
                onChange={e => setSelectedYear(parseInt(e.target.value, 10))}
              >
                {yearOptions.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </Form.Select>
            </Col>

            <Col md={3}>
              <Form.Label>Period</Form.Label>
              <Form.Select
                value={selectedPeriod}
                onChange={e => setSelectedPeriod(e.target.value)}
              >
                <option value="annual">Annual</option>
                <option value="Q1">Q1 (Jan &ndash; Mar)</option>
                <option value="Q2">Q2 (Apr &ndash; Jun)</option>
                <option value="Q3">Q3 (Jul &ndash; Sep)</option>
                <option value="Q4">Q4 (Oct &ndash; Dec)</option>
              </Form.Select>
            </Col>

            {(user?.role === 'admin' || user?.role === 'auditor') && (
              <Col md={3}>
                <Form.Label>Store</Form.Label>
                <Form.Select
                  value={selectedStoreId}
                  onChange={e => setSelectedStoreId(e.target.value)}
                >
                  <option value="">All Stores</option>
                  {stores.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </Form.Select>
              </Col>
            )}

            <Col md={2} className="d-flex align-items-end">
              <Button variant="outline-primary" onClick={loadReport} disabled={loading}>
                {loading ? 'Loading…' : 'Refresh'}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Summary Cards */}
      {summary && (
        <Row className="mb-4 g-3">
          <Col md={3}>
            <Card className="h-100 text-center">
              <Card.Body>
                <div className="text-muted small mb-1">Total Assets</div>
                <div className="fs-4 fw-bold">{fmt(summary.total_purchase_cost)}</div>
                <div className="text-muted small">{summary.total_vehicles} vehicles</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="h-100 text-center">
              <Card.Body>
                <div className="text-muted small mb-1">Period Depreciation</div>
                <div className="fs-4 fw-bold text-danger">{fmt(summary.total_period_depreciation)}</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="h-100 text-center">
              <Card.Body>
                <div className="text-muted small mb-1">Accumulated Depreciation</div>
                <div className="fs-4 fw-bold" style={{ color: '#e67e22' }}>
                  {fmt(summary.total_accumulated_depreciation)}
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="h-100 text-center">
              <Card.Body>
                <div className="text-muted small mb-1">Net Book Value</div>
                <div className="fs-4 fw-bold text-success">{fmt(summary.total_closing_book_value)}</div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Depreciation Table */}
      <Card>
        <Card.Header className="d-flex align-items-center justify-content-between">
          <h5 className="mb-0">
            Vehicle Depreciation Schedule
            {reportData && (
              <span className="text-muted fw-normal fs-6 ms-2">
                &mdash; Period: <strong>{reportData.period}</strong>
              </span>
            )}
          </h5>
          {reportData && (
            <small className="text-muted">
              Rate: {(reportData.ddb_rate * 100).toFixed(0)}% &nbsp;|&nbsp;
              Useful life: {reportData.useful_life_years} years
            </small>
          )}
        </Card.Header>

        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading…</span>
              </div>
              <p className="mt-2 text-muted">Loading depreciation schedule…</p>
            </div>
          ) : reportData?.data?.length > 0 ? (
            <Table striped hover responsive className="mb-0">
              <thead>
                <tr>
                  <th>Vehicle</th>
                  <th>Store</th>
                  <th>Status</th>
                  <th>Acquired</th>
                  <th className="text-end">Purchase Cost</th>
                  <th className="text-end">Yrs in Svc</th>
                  <th className="text-end">Opening BV</th>
                  <th className="text-end">Period Depr.</th>
                  <th className="text-end">Accum. Depr.</th>
                  <th className="text-end">Closing BV</th>
                </tr>
              </thead>
              <tbody>
                {reportData.data.map(v => (
                  <tr key={v.vehicle_id} className={v.fully_depreciated ? 'table-secondary' : ''}>
                    <td>
                      <Link
                        to={`/dashboard/vehicles/${v.vehicle_id}`}
                        style={{ textDecoration: 'none' }}
                      >
                        {v.year} {v.make} {v.model}
                      </Link>
                      {v.fully_depreciated && (
                        <Badge bg="secondary" className="ms-2">Fully Depreciated</Badge>
                      )}
                      <br />
                      <small className="text-muted">{v.vin}</small>
                    </td>
                    <td>{v.store}</td>
                    <td>
                      <Badge bg={statusVariant(v.status)}>{v.status}</Badge>
                    </td>
                    <td>{formatDateLong(v.acquisition_date)}</td>
                    <td className="text-end">{fmt(v.purchase_cost)}</td>
                    <td className="text-end">{v.years_in_service}</td>
                    <td className="text-end">{fmt(v.opening_book_value)}</td>
                    <td className="text-end text-danger">{fmt(v.period_depreciation)}</td>
                    <td className="text-end" style={{ color: '#e67e22' }}>
                      {fmt(v.accumulated_depreciation)}
                    </td>
                    <td className="text-end text-success fw-bold">{fmt(v.closing_book_value)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="p-4 text-center text-muted">
              No vehicles with purchase cost records found for the selected filters.
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}

export default AssetReport;
